import { FC } from 'react';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AgentRoute from './components/AgentRoute';
import SeopRoute from './components/SeopRoute';
import DashboardRoute from './components/DashboardRoute';
import RelatoriosRoute from './components/RelatoriosRoute';
import AppLayout from './components/AppLayout';
import AdminLayout from './components/AdminLayout';
import LoginPage from './pages/LoginPage';
import CadastroMatriculaPage from './pages/CadastroMatriculaPage';
import DashboardPage from './pages/DashboardPage';
import TrailsPage from './pages/TrailsPage';
import TrailDetailPage from './pages/TrailDetailPage';
import RankingPage from './pages/RankingPage';
import PlayHubPage from './pages/PlayHubPage';
import AIOrHumanGamePage from './pages/AIOrHumanGamePage';
import PromptChallengePage from './pages/PromptChallengePage';
import FinalQuizPage from './pages/FinalQuizPage';
import CertificatePage from './pages/CertificatePage';
import CertificateVerifyPage from './pages/CertificateVerifyPage';
import CourseLessonPage from './pages/CourseLessonPage';
import QuizLessonPage from './pages/QuizLessonPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import FeedPage from './pages/FeedPage';
import NotificationsPage from './pages/NotificationsPage';
import AgentsPage from './pages/AgentsPage';
import AgentChatPage from './pages/AgentChatPage';
import {
  SeopBalancePage,
  SeopCapacityPage,
  SeopCatalogPage,
  SeopDashboardPage,
  SeopDemandPage,
  SeopImportPage,
  SeopLayout,
  SeopScenariosPage,
} from './pages/seop';
import AgentCreatePage from './pages/AgentCreatePage';
import AgentConfigPage from './pages/AgentConfigPage';
import AssistantPage from './pages/AssistantPage';
import CompleteProfilePage from './pages/CompleteProfilePage';
import NotFoundPage from './pages/NotFoundPage';
import {
  EquiplexApontamentosPage,
  EquiplexDrePage,
  EquiplexMaquinasPage,
  EquiplexQualidadePage,
  EquiplexSopPage,
  EquiplexVendasPage,
  EquiplexVisaoGeralPage,
} from './pages/paineis';
import { OrigemDadosPage, RelatoriosHomePage } from './pages/relatorios';
import RelatoriosLayout from './components/relatorios/RelatoriosLayout';
import {
  AdminDashboardPage,
  AdminCoursesPage,
  AdminCourseFormPage,
  AdminTagsPage,
  AdminBadgesPage,
  AdminBadgeFormPage,
  AdminUsersPage,
  AdminUserFormPage,
  AdminMissionsPage,
  AdminMissionFormPage,
  AdminLevelsPage,
  AdminSeasonsPage,
  AdminSeasonFormPage,
  AdminSeasonRankingPage,
  AdminBroadcastPage,
  AdminSectorsPage,
  AdminReportsPage,
  AdminCostReportsPage,
  AdminAgentsPage,
  AdminAgentFormPage,
  AdminAgentApprovalsPage,
  AdminAgentQuotaRequestsPage,
  AdminAgentUsagePage,
  AdminAssistantPersonaPage,
} from './pages/admin';

const App: FC = () => {
  return (
    <ThemeProvider>
    <ToastProvider>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro-matricula" element={<CadastroMatriculaPage />} />
          <Route path="/verificar-certificado" element={<CertificateVerifyPage />} />
          <Route path="/verificar-certificado/:code" element={<CertificateVerifyPage />} />
          <Route path="/completar-perfil" element={
            <ProtectedRoute><CompleteProfilePage /></ProtectedRoute>
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <AppLayout>
                <DashboardPage />
              </AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/trilhas" element={
            <ProtectedRoute><AppLayout><TrailsPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/trilhas/:id" element={
            <ProtectedRoute><AppLayout><TrailDetailPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/trilhas/:trailId/aula/:lessonId" element={
            <ProtectedRoute><AppLayout><CourseLessonPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/trilhas/:trailId/aula/:lessonId/quiz" element={
            <ProtectedRoute><AppLayout><QuizLessonPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/ranking" element={
            <ProtectedRoute><AppLayout><RankingPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/jogar" element={
            <ProtectedRoute><AppLayout><PlayHubPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/jogar/ia-ou-humano" element={
            <ProtectedRoute><AppLayout><AIOrHumanGamePage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/jogar/prompt-challenge" element={
            <ProtectedRoute><AppLayout><PromptChallengePage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/quiz/:id" element={
            <ProtectedRoute><AppLayout><FinalQuizPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/certificado/:id" element={
            <ProtectedRoute><AppLayout><CertificatePage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/feed" element={
            <ProtectedRoute><AppLayout><FeedPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/notificacoes" element={
            <ProtectedRoute><AppLayout><NotificationsPage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/perfil" element={
            <ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>
          } />
          <Route path="/perfil/:userId" element={
            <ProtectedRoute><AppLayout><PublicProfilePage /></AppLayout></ProtectedRoute>
          } />
          {/* Assistente Geral "H.Bot" (Frente 3) — acessível a todos, separado de /agentes */}
          <Route path="/assistente" element={
            <ProtectedRoute><AppLayout hideFooter><AssistantPage /></AppLayout></ProtectedRoute>
          } />
          {/* J07: assistente específico (H.Lex…) resolvido por id — reusa a mesma UI */}
          <Route path="/assistente/:agentId" element={
            <ProtectedRoute><AppLayout hideFooter><AssistantPage /></AppLayout></ProtectedRoute>
          } />
          {/* Hub de Agentes (Fase 2 — qualquer learner; visibilidade via ACL server-side) */}
          <Route path="/agentes" element={
            <AgentRoute><AppLayout><AgentsPage /></AppLayout></AgentRoute>
          } />
          <Route path="/agentes/novo" element={
            <AgentRoute><AppLayout><AgentCreatePage /></AppLayout></AgentRoute>
          } />
          <Route path="/agentes/:id/configurar" element={
            <AgentRoute><AppLayout><AgentConfigPage /></AppLayout></AgentRoute>
          } />
          <Route path="/agentes/:id" element={
            <AgentRoute><AppLayout hideFooter><AgentChatPage /></AppLayout></AgentRoute>
          } />
          {/* Balanceamento de Linha (frente SEOP) — sem item na Sidebar (B-D2), só link
              direto. O mês selecionado viaja na query string (`?mes=`), porque estas
              rotas são planas e o SeopLayout remonta a cada troca de aba. */}
          <Route path="/balanceamento-linha" element={
            <SeopRoute><AppLayout><SeopLayout><SeopDashboardPage /></SeopLayout></AppLayout></SeopRoute>
          } />
          <Route path="/balanceamento-linha/balanceamento" element={
            <SeopRoute><AppLayout><SeopLayout><SeopBalancePage /></SeopLayout></AppLayout></SeopRoute>
          } />
          <Route path="/balanceamento-linha/demanda" element={
            <SeopRoute><AppLayout><SeopLayout><SeopDemandPage /></SeopLayout></AppLayout></SeopRoute>
          } />
          {/* Sub-rota da Demanda: o import é um fluxo de 3 passos, não uma aba. */}
          <Route path="/balanceamento-linha/demanda/importar" element={
            <SeopRoute><AppLayout><SeopLayout><SeopImportPage /></SeopLayout></AppLayout></SeopRoute>
          } />
          <Route path="/balanceamento-linha/capacidade" element={
            <SeopRoute><AppLayout><SeopLayout><SeopCapacityPage /></SeopLayout></AppLayout></SeopRoute>
          } />
          <Route path="/balanceamento-linha/cenarios" element={
            <SeopRoute><AppLayout><SeopLayout><SeopScenariosPage /></SeopLayout></AppLayout></SeopRoute>
          } />
          <Route path="/balanceamento-linha/cadastros" element={
            <SeopRoute><AppLayout><SeopLayout><SeopCatalogPage /></SeopLayout></AppLayout></SeopRoute>
          } />
          {/* Relatórios — fora da Sidebar, só link direto. A home é o índice de tudo
              (painéis + Balanceamento) e entra quem tem QUALQUER uma das flags; cada
              relatório mantém o seu próprio guard. As rotas `/paineis/...` continuam
              valendo: são os links que já estão circulando com o pessoal da fábrica. */}
          <Route path="/relatorios" element={
            <RelatoriosRoute><AppLayout><RelatoriosLayout><RelatoriosHomePage /></RelatoriosLayout></AppLayout></RelatoriosRoute>
          } />
          <Route path="/relatorios/origem-dos-dados" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><OrigemDadosPage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis" element={<Navigate to="/relatorios" replace />} />
          <Route path="/paineis/equiplex" element={<Navigate to="/paineis/equiplex/visao-geral" replace />} />
          <Route path="/paineis/equiplex/visao-geral" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexVisaoGeralPage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis/equiplex/maquinas" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexMaquinasPage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis/equiplex/sop" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexSopPage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis/equiplex/vendas" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexVendasPage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis/equiplex/dre" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexDrePage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis/equiplex/qualidade" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexQualidadePage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          <Route path="/paineis/equiplex/apontamentos" element={
            <DashboardRoute><AppLayout><RelatoriosLayout><EquiplexApontamentosPage /></RelatoriosLayout></AppLayout></DashboardRoute>
          } />
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminRoute><AdminLayout><AdminDashboardPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/cursos" element={<AdminRoute><AdminLayout><AdminCoursesPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/cursos/novo" element={<AdminRoute><AdminLayout><AdminCourseFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/cursos/:id/editar" element={<AdminRoute><AdminLayout><AdminCourseFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/agentes" element={<AdminRoute><AdminLayout><AdminAgentsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/agentes/aprovacoes" element={<AdminRoute><AdminLayout><AdminAgentApprovalsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/agentes/pedidos" element={<AdminRoute><AdminLayout><AdminAgentQuotaRequestsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/agentes/novo" element={<AdminRoute><AdminLayout><AdminAgentFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/agentes/:id/editar" element={<AdminRoute><AdminLayout><AdminAgentFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/agentes/:id/uso" element={<AdminRoute><AdminLayout><AdminAgentUsagePage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/assistente" element={<AdminRoute><AdminLayout><AdminAssistantPersonaPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/tags" element={<AdminRoute><AdminLayout><AdminTagsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/badges" element={<AdminRoute><AdminLayout><AdminBadgesPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/badges/novo" element={<AdminRoute><AdminLayout><AdminBadgeFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/badges/:id/editar" element={<AdminRoute><AdminLayout><AdminBadgeFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/usuarios" element={<AdminRoute><AdminLayout><AdminUsersPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/usuarios/novo" element={<AdminRoute><AdminLayout><AdminUserFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/usuarios/:id/editar" element={<AdminRoute><AdminLayout><AdminUserFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/missoes" element={<AdminRoute><AdminLayout><AdminMissionsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/missoes/nova" element={<AdminRoute><AdminLayout><AdminMissionFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/missoes/:id/editar" element={<AdminRoute><AdminLayout><AdminMissionFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/niveis" element={<AdminRoute><AdminLayout><AdminLevelsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/temporadas" element={<AdminRoute><AdminLayout><AdminSeasonsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/temporadas/nova" element={<AdminRoute><AdminLayout><AdminSeasonFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/temporadas/:id/editar" element={<AdminRoute><AdminLayout><AdminSeasonFormPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/temporadas/:id/ranking" element={<AdminRoute><AdminLayout><AdminSeasonRankingPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/setores" element={<AdminRoute><AdminLayout><AdminSectorsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/notificacoes" element={<AdminRoute><AdminLayout><AdminBroadcastPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/relatorios" element={<AdminRoute><AdminLayout><AdminReportsPage /></AdminLayout></AdminRoute>} />
          <Route path="/admin/custos-agentes" element={<AdminRoute><AdminLayout><AdminCostReportsPage /></AdminLayout></AdminRoute>} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
